declare var $: any;
//system
import { Component, ViewChild, HostListener, OnInit, OnDestroy, ViewEncapsulation, AfterViewInit, ElementRef, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import {Title, Meta} from "@angular/platform-browser";
import { firstValueFrom, forkJoin, from, of, Subject, Subscription, takeUntil } from 'rxjs';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Location } from '@angular/common';

//services
import { LogService } from 'src/app/services/common/log.service';
import { AlertService } from 'src/app/services/alert.service';
import { AuthService } from 'src/app/services/auth.service';
import { StudioService } from 'src/app/services/studio.service';
import { FacilityService } from 'src/app/services/facility.service';
import { MemberService } from 'src/app/services/member.service';
import { InviteCodeService } from 'src/app/services/invite-code.service';

//interfaces
import { SessionDetails, ClassMember, SessionWaitlist, Amenity} from 'src/app/models/studio/session-details';
import { isPartnerArtifact } from 'src/app/models/studio/shared-schedule';
import { ClassMemberModel } from 'src/app/models/studio/class-booking';
import { UtcTimeModel } from 'src/app/models/common-models';
import { Booking, MemberBalance, MemberBasicInfo, MemberCurrency, MyDrop } from 'src/app/models/member';
import { Action, InviteCodeIntent, MembershipAction, UserType } from 'src/app/models/enums';
import { InviteCode, InviteCodeGenerateRequest, InviteCodeRedeemRequest, InviteDialogModel } from 'src/app/models/invite-code';

//components
import { AuthBaseComponent } from 'src/app/components/auth-base/auth-base.component';
import { SpotGridComponent } from 'src/app/components/studio/spot-grid/spot-grid.component';
import { CancelReservationComponent } from 'src/app/components/studio/cancel-reservation/cancel-reservation.component';
import { InviteComponent } from 'src/app/components/invite/invite.component';
import { LoginComponent } from 'src/app/components/df-login/df-login.component';
import { PresaleBookComponent } from 'src/app/components/df-presale-book/df-presale-book.component';

//utils
import { Utils } from 'src/app/services/common/utils';
import { DownloadAppComponent } from 'src/app/components/download-app/download-app.component';
import { SessionWaitlistService } from 'src/app/services/session-waitlist.service';
import { SessionWaitlistAddedComponent } from 'src/app/components/popups/session-waitlist-added/session-waitlist-added.component';
import { EnvironmentService } from 'src/app/services/environment.service';
import { ConfirmReservationComponent } from 'src/app/components/studio/confirm-reservation/confirm-reservation.component';
import { FacilityConfig } from 'src/environments/environment.interface';

import { UserService } from 'src/app/services/user.service';
import { MetaService } from 'src/app/services/meta.service';
import { CurrencyMeta } from 'src/app/models/meta';
import { LocalStorageService } from 'src/app/services/local-storage.service';

@Component({
  selector: 'app-class-details',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './class-details.component.html',
  styleUrls: ['./class-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class ClassDetailsComponent extends AuthBaseComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('login') loginPopup!: LoginComponent;
  @ViewChild('presaleBook') presaleBookPopup!: PresaleBookComponent;
  @ViewChild('download') downloadPopup!: DownloadAppComponent;
  private readonly _unsubscribe: Subject<any> = new Subject();
  isLoading: boolean = false;
  sessionId: number = 0;
  dayOfWeek: string = "";
  directionUrl: string = '';
  month: string = "";
  dayOfMonth: string = "";
  startTime: string = "";
  endTime: string = "";
  timeZoneAbbr: string = "";
  /** IANA TZ for the session's studio location (resolved from Studio.LocationId). */
  private sessionLocationTimeZone: string | undefined;
  utcTime!: UtcTimeModel;
  currentMember!: any;
  classMember!: ClassMember;
  inviteCode!: InviteCode;
  inviteCodeStr: string = "";
  locationId: number = 1;
  friendsInfo!: MemberBasicInfo[];
  friendIds: number[] = [];
  isSessionBooked: boolean = false;
  sessionWaitlist: SessionWaitlist[] = [];
  isInWaitlist: boolean = false;
  pendingWaitlistAction: boolean = false; // Flag to track if user wants to join waitlist after login
  /** Signed-out locked RESERVE: after login, re-check Premium and capacity before any booking UI. */
  pendingPremiumUpgradeAction: boolean = false;
  facilityConfig: FacilityConfig;
  amenities: Amenity[] = [];

  currencyMeta:           CurrencyMeta = <CurrencyMeta>{};
  classesRemaining:       string = '';
  memberBalance:          MemberBalance | null = null;
  myDrop:                 MyDrop | null = null;

  private inviteCodeSubscription!: Subscription;
  private facilitySubscription!: Subscription;

  sessionDetails!: SessionDetails;
  spotsLeft: number = 0;
  allowInviteFriends: boolean = false;

  hideTimeout: number = 0;
  isIframeRoute: boolean = false;
  bodyHeight: number = 500;
  logo: string = '';
  submitted: boolean = false;

  // Performance optimization: Cache member lookups
  private _cachedMember: any = null;
  private _memberCacheTime: number = 0;
  private readonly MEMBER_CACHE_TTL = 1000; // 1 second cache

  // Performance optimization: Memoize date calculations
  private _dateFormats: {
    dayOfWeek?: string;
    month?: string;
    dayOfMonth?: string;
    startTime?: string;
    endTime?: string;
  } = {};

  // Performance optimization: Use Set for O(1) lookups
  private bookedSpotsSet = new Set<number>();
  private classMemberMap = new Map<number, ClassMember>();

  constructor(
    private router: Router
    , private logService: LogService
    , private alertService: AlertService
    , private titleService: Title
    , private metaService: Meta
    , private authService: AuthService
    , private facilityService: FacilityService
    , private modalService: NgbModal
    , private activeRoute: ActivatedRoute
    , public activeModal: NgbActiveModal
    , private studioService: StudioService
    , private memberService: MemberService
    , private inviteCodeService: InviteCodeService
    , private userService: UserService
    , private _metaService: MetaService
    , private sessionWaitlistService: SessionWaitlistService
    , private _environmentService: EnvironmentService
    , private location: Location
    , private el: ElementRef
    , private cdr: ChangeDetectorRef
    , private localStorageService: LocalStorageService
  ) 
  { 
    super(router, logService, alertService, facilityService);
  }

  override async ngOnInit() {
    await super.ngOnInit();

    const currentRoute = this.router.url;
    this.isIframeRoute = currentRoute.includes('-iframe');
    
    this.locationId = this.activeRoute.snapshot.queryParams['location_id'];
    this.inviteCodeStr = this.activeRoute.snapshot.queryParams['invite_code'];

    this.activeRoute.params.subscribe(params => {
      this.sessionId = +params['sessionId'];
    });

    this.facilityConfig = this._environmentService.getFacilityConfig();
    this.allowInviteFriends = this.facilityConfig.allowInviteFriends;
    this.logo = this._environmentService.getLinkUrlById('smallLogo');

    this.isLoading = true;
    this.currentMember = this.authService.getLoggedInMember();

    forkJoin({
      sessionDetails: this.studioService.getClassDetails(this.sessionId),
      myDrop: this.isLoggedIn()
        ? from(this.memberService.getMyDrop(this.currentMember.Id))
        : of(null),
      currencyMeta: this.isLoggedIn() ? this._metaService.getCurrencyMetaByMember(this.userService.getUser().Id) : of(null),
    }).pipe(
      takeUntil(this._unsubscribe)
    ).subscribe({
      next: (results) => {
        void this.applyClassDetailsResults(results);
      },
      error: (err: any) => {
        this.isLoading = false;
        console.error(err);
        this.cdr.markForCheck();
      }
    }); 
  }

  private async applyClassDetailsResults(results: {
    sessionDetails: SessionDetails;
    myDrop: MyDrop | null;
    currencyMeta: CurrencyMeta | null;
  }): Promise<void> {
    try {
      this.sessionDetails = results.sessionDetails;
      this.myDrop = results.myDrop;
      if (results.currencyMeta) {
        this.currencyMeta = results.currencyMeta;
      }
      if (this.isLoggedIn()) {
        const currencyId = this.sessionDetails?.Class?.CurrencyId || 1;
        this.memberBalance = await firstValueFrom(
          this.memberService.getBalance(this.currentMember.Id, currencyId)
        );
      }

      // Optimize amenities processing - single pass
      this.amenities = this.sessionDetails.Class.ClassAmenities.map(ca => ca.Amenity).sort((a, b) => a.Order - b.Order);
      this.directionUrl = this.getDirectionUrl();
      this.spotsLeft = this.sessionDetails.SpotConfig.TotalSpots - this.sessionDetails.ClassMembers.length;
      this.spotsLeft = this.spotsLeft < 0 ? 0 : this.spotsLeft;

      // Build lookup structures for O(1) access
      this.classMemberMap.clear();
      this.bookedSpotsSet.clear();
      this.sessionDetails.ClassMembers.forEach(classMember => {
        this.classMemberMap.set(classMember.MemberId, classMember);
        this.bookedSpotsSet.add(classMember.Spot);
      });

      await this.resolveSessionLocationTimeZone();

      // Facility wall-clock → absolute instant → display in the visitor's local timezone.
      const display = Utils.formatSessionDisplayParts(
        this.sessionDetails.StartDate,
        this.sessionDetails.StartTime,
        this.sessionDetails.EndTime,
        this.getSessionTimeZone()
      );
      this._dateFormats.dayOfWeek = display.dayOfWeek;
      this._dateFormats.month = display.month;
      this._dateFormats.dayOfMonth = display.dayOfMonth;
      this._dateFormats.startTime = display.startTime;
      this._dateFormats.endTime = display.endTime;
      this.dayOfWeek = display.dayOfWeek;
      this.month = display.month;
      this.dayOfMonth = display.dayOfMonth;
      this.startTime = display.startTime;
      this.endTime = display.endTime;
      this.timeZoneAbbr = display.timeZoneAbbr;

      this.setMetaTags();

      if (this.isLoggedIn()) {
        // Use Map for O(1) lookup instead of forEach
        const memberClassMember = this.classMemberMap.get(this.currentMember.Id);
        if (memberClassMember) {
          this.isSessionBooked = true;
          this.classMember = memberClassMember;

          if (this.currentMember.OnboardedAt === '0001-01-01T00:00:00' && this._environmentService.getProperty('submerchant') === 'dropfitness') {
            if (this.downloadPopup) {
              window.setTimeout(() => this.downloadPopup.open(), 100);
              this.hideTimeout = window.setTimeout(() => this.downloadPopup.dismiss(), 10000);
            }
          }
        }

        if (this.spotsLeft === 0) {
          this.sessionWaitlist = await this.sessionWaitlistService.get(this.sessionId);
          // Use find instead of forEach for early exit
          const waitlistEntry = this.sessionWaitlist.find(row => Number(row.MemberId) === Number(this.currentMember.Id));
          if (waitlistEntry) {
            this.isInWaitlist = true;
          }
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  private scrollThrottleTimer: any = null;

  @HostListener('window:scroll', ['$event'])
  onScroll(event : any) {
    // Throttle scroll events for better performance
    if (this.scrollThrottleTimer) {
      return;
    }
    this.scrollThrottleTimer = setTimeout(() => {
      Utils.makeElementFloating('.footer-area', '.floating-footer');
      this.scrollThrottleTimer = null;
    }, 100);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.sendBodyHeight();
  }

  ngAfterViewInit(): void {
    this.sendBodyHeight();
    setTimeout(() => {
      this.sendBodyHeight();
    }, 1000);
  }

  private resizeObserver: ResizeObserver | null = null;
  private resizeThrottleTimer: any = null;

  sendBodyHeight() {
    // Throttle resize observer for better performance
    if (this.resizeThrottleTimer) {
      return;
    }

    this.resizeThrottleTimer = setTimeout(() => {
      const bodyH = this.el.nativeElement.ownerDocument.body.offsetHeight;
      if (this.bodyHeight !== bodyH) {
        this.bodyHeight = bodyH;
        window.parent.postMessage({ frameHeight: this.bodyHeight }, '*');
      }
      this.resizeThrottleTimer = null;
    }, 100);

    // Only create observer once
    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        const bodyH = this.el.nativeElement.ownerDocument.body.offsetHeight;
        if (this.bodyHeight !== bodyH) {
          this.bodyHeight = bodyH;
          window.parent.postMessage({ frameHeight: this.bodyHeight }, '*');
        }
      });
      this.resizeObserver.observe(this.el.nativeElement.ownerDocument.body);
    }
  }

  getDirectionUrl(): string {
    return `https://www.google.com/maps/dir/?api=1&destination=${this.sessionDetails?.Class?.Address?.Latitude},${this.sessionDetails?.Class?.Address?.Longitude}`;
  }

  isLoggedIn(): boolean {
    // Cache member lookup for performance
    const now = Date.now();
    if (this._cachedMember && (now - this._memberCacheTime) < this.MEMBER_CACHE_TTL) {
      this.currentMember = this._cachedMember;
      return !!this._cachedMember;
    }
    this.currentMember = this.authService.getLoggedInMember();
    this._cachedMember = this.currentMember;
    this._memberCacheTime = now;
    return !!this.currentMember;
  }

  /** Load IANA TZ for the session studio's location (not the visitor's selected location). */
  private async resolveSessionLocationTimeZone(): Promise<void> {
    this.sessionLocationTimeZone = undefined;
    const locationId = this.sessionDetails?.Studio?.LocationId;
    if (!locationId) {
      return;
    }
    try {
      const locations = await this.facilityService.getLocations();
      this.sessionLocationTimeZone = locations.find((loc) => loc.Id === locationId)?.TimeZone;
    } catch (err) {
      this.logService.error('Failed to resolve session location timezone', err);
    }
  }

  /** IANA timezone for the session's gym — session location first, then facility default. */
  private getSessionTimeZone(): string {
    return Utils.resolveSessionTimeZone(
      this.sessionLocationTimeZone,
      this._environmentService.getFacilityConfig()?.timezone
    );
  }

  isInFuture(): boolean {
    return Utils.isSessionStartInFutureInTimezone(
      this.sessionDetails.StartDate,
      this.sessionDetails.StartTime,
      this.getSessionTimeZone()
    );
  }

  async openSpotMapClicked() {
    if(this.isLoggedIn())
    {
      await this.refreshMembershipData();
      this.currentMember = this.authService.getLoggedInMember();
      // Use Map for O(1) lookup instead of forEach
      const memberClassMember = this.classMemberMap.get(this.currentMember.Id);
      if(memberClassMember) {
        this.isSessionBooked = true;
      }

      if(this.inviteCodeStr && this.currentMember)
      {
        const redeemRequest : InviteCodeRedeemRequest = {
          MemberId : this.currentMember.Id,
          Code: this.inviteCodeStr
        };

        if(!this.isSessionBooked)
        {
          this.inviteCodeService.redeemInviteCode(redeemRequest).subscribe((data: InviteCode | string) => {
            if(typeof(data) === 'string')
            {
              this.alertService.error("You do not have enough credits to book a class.");
              return;
            } else {
              this.inviteCode = data;
              this.logService.debug("this.inviteCode", this.inviteCode);
            }
            this.memberService.getMutual(this.currentMember.Id).subscribe((data: MemberBasicInfo[]) => {
              this.friendsInfo = data;
              this.friendIds = this.friendsInfo.map(c => c.Id);
              this.logService.debug("this.friendIds",this.friendIds);
              this.openSpotMap();
            });
          });
        }
      } else {
        if(!this.isSessionBooked)
        {
          this.openSpotMap();
        }
      }
    } else {
      // get prospect info from session storage
      var prospectInfo = sessionStorage.getItem('prospectInfo');
      if (prospectInfo && this.facilityConfig.getStartedPage.allowFreeClassBooking) {
        prospectInfo = JSON.parse(prospectInfo);
        this.presaleBookPopup.open(this.sessionDetails, prospectInfo);
      } else {
        this.loginPopup.open(this.sessionDetails.Id);
      }
    }
  }

  openSpotMap(delay: number = 0) {
    setTimeout(async () => {
      if (this.isLoggedIn()) {
        await this.refreshMembershipData();
      }

      if(!this.sessionDetails.Class.ShowSpotSelection)
      {
        //open confirm reservation popup
        const modalRef =this.modalService.open(ConfirmReservationComponent, {
          ariaLabelledBy: 'modal-basic-title', 
          // size: 'lg',
          centered: true,
          fullscreen: window.innerWidth <= 768,
          // windowClass: 'custom-class'
        });
    
        //Passing sessionDetails to popup
        modalRef.componentInstance.sessionDetails = this.sessionDetails;
        modalRef.componentInstance.selectedSpots = [];
        modalRef.componentInstance.membershipPaused = this.isMembershipPausedForClass();
    
        //Retrieving ClassMember from popup
        modalRef.result.then((result: ClassMemberModel | undefined | null) => {
          if(typeof result == "undefined")
          {
            return;
          }
          
          if (result === null) {
            return;
          }
          if (result) {
            this.logService.debug("class booking details:", result);
            location.reload();
          }
        });
        return;
      }
      
      //open class map popup
      const modalRef = this.modalService.open(SpotGridComponent, {
        ariaLabelledBy: 'modal-basic-title',
        size: 'md',
        centered: true,
        fullscreen: window.innerWidth <= 768,
        windowClass: 'spot-grid-modal-wrapper',
        modalDialogClass: 'spot-grid-modal-dialog'
      });

      //Passing sessionDetails to popup
      modalRef.componentInstance.sessionDetails = this.sessionDetails;
      modalRef.componentInstance.friendIds = this.friendIds;
      modalRef.componentInstance.balance = this.getBalance();
      modalRef.componentInstance.membershipPaused = this.isMembershipPausedForClass();
  
      //Retrieving ClassMember from popup
      modalRef.result.then((result: ClassMemberModel) => {
        if (result) {
          this.logService.debug("class booking details:", result);
          location.reload();
        }
      });
    }, delay);
  }

  cancelReservation(classMemberId: number) {
    this.logService.debug("Cancel class member id:", classMemberId);

    //open class map popup
    const modalRef =this.modalService.open(CancelReservationComponent, {
      ariaLabelledBy: 'modal-basic-title', 
      // size: 'lg',
      centered: true,
      fullscreen: window.innerWidth <= 768,
      // windowClass: 'custom-class'
    });

    //Passing sessionDetails & classMember to popup
    modalRef.componentInstance.sessionDetails = this.sessionDetails;
    modalRef.componentInstance.classMember = this.classMember;

    //Retrieving ClassMember from popup
    modalRef.result.then((result: boolean) => {
      if (result) {
        this.logService.debug("class booking details:", result);
        location.reload();
      }
    });
  }

  setMetaTags() {
    const display = Utils.formatSessionDisplayParts(
      this.sessionDetails.StartDate,
      this.sessionDetails.StartTime,
      this.sessionDetails.EndTime,
      this.getSessionTimeZone()
    );
    const formttedStartDate = `${display.month} ${display.dayOfMonth}`;
    const formttedStartTime = display.timeZoneAbbr
      ? `${display.startTimeFull} ${display.timeZoneAbbr}`
      : display.startTimeFull;
    
    var title = this.sessionDetails.Studio.Name + " - "+ this.sessionDetails.Class.Name + " at " + this._environmentService.getFacilityConfig().name + " " + formttedStartDate + " at " + formttedStartTime;
    var websiteUrl = this._environmentService.getLinkById('website');
    var url = websiteUrl + "studio/class-details/" + this.sessionDetails.Id;
    
    //title
    this.titleService.setTitle(title);
    
    //description
    this.metaService.updateTag({ name: 'description', content: this.sessionDetails.Class.Description });

    //og tags
    this.metaService.updateTag({ property: "og:title", content: title});
    this.metaService.updateTag({ property: "og:description", content: this.sessionDetails.Class.Description });
    this.metaService.updateTag({ property: "og:image", content: this.sessionDetails.ClassPhoto });
    this.metaService.updateTag({ property: "og:url", content: url });

    //twitter tags
    this.metaService.updateTag({ name: "twitter:title", content: title });
    this.metaService.updateTag({ name: "twitter:description", content: this.sessionDetails.Class.Description });
    this.metaService.updateTag({ name: "twitter:image", content: this.sessionDetails.ClassPhoto });
    this.metaService.updateTag({ name: "twitter:url", content: url });
  }

  ngOnDestroy() {
    if (this.facilitySubscription) {
      this.facilitySubscription.unsubscribe();
    }

    if (this.inviteCodeSubscription) {
      this.inviteCodeSubscription.unsubscribe();
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.resizeThrottleTimer) {
      clearTimeout(this.resizeThrottleTimer);
    }

    if (this.scrollThrottleTimer) {
      clearTimeout(this.scrollThrottleTimer);
    }

    this._unsubscribe.next(null);
    this._unsubscribe.complete();
  }

  trackByAmenityId(index: number, amenity: Amenity): number {
    return amenity.Id;
  }

  loggedIn(isLoggedIn: boolean): Promise<void> {
    if (!isLoggedIn) {
      return Promise.resolve();
    }
    return this.handleLoggedIn();
  }

  private async handleLoggedIn(): Promise<void> {
    this.currentMember = this.authService.getLoggedInMember();
    this._cachedMember = null;
    this._memberCacheTime = 0;
    const memberClassMember = this.classMemberMap.get(this.currentMember.Id);
    if (memberClassMember) {
      this.isSessionBooked = true;
      this.classMember = memberClassMember;
    }

    if (this.pendingWaitlistAction) {
      this.pendingWaitlistAction = false;
      this.pendingPremiumUpgradeAction = false;
      this.joinWaitlistAfterLogin();
    } else if (this.pendingPremiumUpgradeAction) {
      await this.resumePremiumIntentAfterLogin();
    } else if (!this.isSessionBooked) {
      this.openSpotMap(500);
    }
    this.cdr.markForCheck();
  }

  /** Locked RESERVE was clicked while signed out. Refresh entitlement before booking UI. */
  private async resumePremiumIntentAfterLogin(): Promise<void> {
    this.pendingPremiumUpgradeAction = false;
    try {
      await this.refreshMembershipData();
      if (this.spotsLeft === 0 && this.isLoggedIn()) {
        this.sessionWaitlist = await this.sessionWaitlistService.get(this.sessionId);
        const memberId = Number(this.currentMember?.Id);
        this.isInWaitlist = (this.sessionWaitlist || []).some((row) => Number(row.MemberId) === memberId);
      }
    } catch (err) {
      console.error(err);
    }
    this.cdr.markForCheck();

    if (this.isSessionBooked) {
      return;
    }
    if (this.requiresPremiumUpgrade) {
      this.promptPremiumUpgrade();
      return;
    }
    if (this.spotsLeft === 0) {
      if (!this.isInWaitlist) {
        this.JoinWaitlistClicked();
      }
      return;
    }
    this.openSpotMap(500);
  }

  private joinWaitlistAfterLogin(): void {
    if (!this.currentMember || this.isSessionBooked) {
      return;
    }
    const request: Partial<SessionWaitlist> = {
      MemberId: this.currentMember.Id,
      SessionId: this.sessionId,
      Action: Action.Add
    };
    this.inviteCodeSubscription = this.sessionWaitlistService.add(request).subscribe((result: boolean) => {
      if (result) {
        this.isInWaitlist = true;
        this.cdr.markForCheck();
        const modalRef = this.modalService.open(SessionWaitlistAddedComponent, {
          ariaLabelledBy: 'modal-basic-title',
          centered: true,
          fullscreen: window.innerWidth <= 768,
        });
        modalRef.componentInstance.classPhoto = this.sessionDetails?.ClassPhoto;
        modalRef.componentInstance.heading = "You're on the Waitlist!";
        modalRef.componentInstance.subHeading = "We will notify everyone on the waitlist as soon as a spot becomes available.";
        modalRef.result.then(() => undefined);
      }
    });
  }

  booked(isBooked: boolean) {
    // Handle presale booking logic here
    console.log('Presale booking event:', isBooked);
  }

  inviteFriends() {
    this.submitted = true;
    this.inviteCodeService.generateInviteCode(new InviteCodeGenerateRequest(
      UserType.Member,
      this.currentMember.Id,
      InviteCodeIntent.ClassSession,
      this.sessionDetails.Id,
      -1
    )).subscribe(async (data : InviteCode) => {
      this.inviteCode = data;

      let openGraphMetadata = await this.inviteCodeService.getOpenGraphMetadata(this.inviteCode.Code);

      //open invite friends popup
      const modalRef =this.modalService.open(InviteComponent, {
        ariaLabelledBy: 'modal-basic-title', 
        size: 'md',
        centered: true,
        fullscreen: window.innerWidth <= 768,
        windowClass: 'invite-modal-wrapper',
        modalDialogClass: 'invite-modal-dialog'
      });

      const display = Utils.formatSessionDisplayParts(
        this.sessionDetails.StartDate,
        this.sessionDetails.StartTime,
        this.sessionDetails.EndTime,
        this.getSessionTimeZone()
      );
      const whenLabel = display.timeZoneAbbr
        ? `${display.dayOfWeek}, ${display.month} ${display.dayOfMonth} at ${display.startTimeFull} ${display.timeZoneAbbr}`
        : `${display.dayOfWeek}, ${display.month} ${display.dayOfMonth} at ${display.startTimeFull}`;
      var title = this.sessionDetails.Studio.Name + " - " + this.sessionDetails.Class.Name + " at " + this.sessionDetails.Studio.Name + " " + whenLabel;
      var emailTitle = 'Hey! Join me at ' + this.sessionDetails.Studio.Name + '!🙌';
      var emailBody = 'Hey! Join me for ' + this.sessionDetails.Class.Name + ' at ' + this.sessionDetails.Studio.Name + ' on ' + whenLabel + ' inside ' + this.sessionDetails.Studio.Name + '. Use the link below to book your spot! ' + this.getSharerUrl();
      //Passing InviteCode and InviteDialogModel to popup
      modalRef.componentInstance.inviteCode = this.inviteCode;
      modalRef.componentInstance.inviteDialogModel = new InviteDialogModel(
        openGraphMetadata.Title,
        openGraphMetadata.Description,
        openGraphMetadata.ImageUrl,
        openGraphMetadata.Url,
        emailTitle,
        emailBody
      );

      //Retrieving result from popup
      modalRef.result.then((result: void) => {
          this.submitted = false;
          this.cdr.markForCheck();
      }).catch((reason: any) => {
          // Handle modal dismissal (clicking outside, pressing ESC, etc.)
          this.submitted = false;
          this.cdr.markForCheck();
      });
    });
  }

  goBack() : void {
    this.location.back();
  }

  getSharerUrl() : string
  {
    return this._environmentService.getFacilityConfig().sharerBaseUrl + "share?invite_code=" + this.inviteCode.Code + "&location_id=" + 1
  }

  spotsLeftText(): string {
    return `${this.spotsLeft} spot${this.spotsLeft !== 1 ? 's' : ''} left`;
  }

  /** Partner class + no Premium: locked RESERVE (even when waitlist is on). */
  get requiresPremiumUpgrade(): boolean {
    if (!this.sessionDetails) {
      return false;
    }
    const partner =
      isPartnerArtifact(this.sessionDetails) ||
      isPartnerArtifact(this.sessionDetails.Class) ||
      isPartnerArtifact(this.sessionDetails.Studio);
    if (!partner) {
      return false;
    }
    return !this.hasActivePremiumEntitlement();
  }

  /** Upgrade CTA. Hidden when the member is already on the waitlist so they can leave. */
  showLockedReserve(): boolean {
    return this.isInFuture() && this.requiresPremiumUpgrade && !this.isSessionBooked && !this.isInWaitlist;
  }

  showJoinWaitlist(): boolean {
    return this.isInFuture() && !this.requiresPremiumUpgrade && this.spotsLeft === 0 && !this.isInWaitlist && !this.isSessionBooked;
  }

  /**
   * Leaving the waitlist is not gated on Premium.
   * An entitled member with an open spot still books through RESERVE.
   */
  showCancelWaitlist(): boolean {
    if (!this.isInFuture() || !this.isInWaitlist || this.isSessionBooked) {
      return false;
    }
    return this.spotsLeft === 0 || this.requiresPremiumUpgrade;
  }

  showReserve(): boolean {
    return this.isInFuture() && !this.requiresPremiumUpgrade && this.spotsLeft > 0 && !this.isSessionBooked;
  }

  /**
   * Same rules as MemberHasActivePremiumMenuPack: uncancelled currency,
   * StartAt through today, EndAt or ExpireAt through today, and balance or a subscription booking.
   */
  private hasActivePremiumEntitlement(now = new Date()): boolean {
    if (!this.myDrop) {
      return false;
    }
    if ((this.myDrop.Packs || []).some((pack) => this.isActivePremiumCurrency(pack, now))) {
      return true;
    }
    return (this.myDrop.Subscriptions || []).some((subscription) =>
      (subscription.Cycles || []).some((cycle) =>
        this.isActivePremiumCurrency(
          {
            ...cycle,
            IsPremium: cycle.IsPremium === true || subscription.IsPremium === true,
            BookingId: cycle.BookingId ?? subscription.Id,
          },
          now
        )
      )
    );
  }

  private isActivePremiumCurrency(
    row: Partial<MemberCurrency> | null | undefined,
    now: Date
  ): boolean {
    if (!row || row.IsPremium !== true || row.IsCancelled) {
      return false;
    }
    if (!this.premiumWindowCoversToday(row.StartAt, row.EndAt, row.ExpireAt, now)) {
      return false;
    }
    const balance = Number(row.Balance ?? 0);
    const bookingId = Number(row.BookingId ?? 0);
    return balance > 0 || bookingId > 0;
  }

  /** Inclusive UTC calendar days. ExpireAt extends access when EndAt is already past. */
  private premiumWindowCoversToday(
    startAt: string | Date | undefined,
    endAt: string | Date | undefined,
    expireAt: string | Date | undefined,
    now: Date
  ): boolean {
    const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    const start = this.utcCalendarDay(startAt);
    const ends = [this.utcCalendarDay(endAt), this.utcCalendarDay(expireAt)].filter(
      (day): day is number => day != null
    );
    if (start == null || ends.length === 0) {
      return false;
    }
    const end = Math.max(...ends);
    return start <= today && today <= end;
  }

  private utcCalendarDay(value: string | Date | undefined | null): number | null {
    if (value == null || value === '') {
      return null;
    }
    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) {
        return null;
      }
      return Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate());
    }
    const text = value.trim();
    if (!text || text.startsWith('0001-01-01')) {
      return null;
    }
    const hasZone = /(?:z|Z|[+-]\d{2}:?\d{2})$/.test(text);
    let normalized = text;
    if (!hasZone) {
      if (text.includes(' ') && !text.includes('T')) {
        normalized = `${text.replace(' ', 'T')}Z`;
      } else if (text.includes('T')) {
        normalized = `${text}Z`;
      } else {
        normalized = `${text}T00:00:00Z`;
      }
    }
    const parsed = new Date(normalized);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
  }

  openPremiumUpgrade(): void {
    if (!this.isLoggedIn()) {
      this.pendingPremiumUpgradeAction = true;
      this.loginPopup.open(this.sessionDetails?.Id);
      return;
    }
    this.promptPremiumUpgrade();
  }

  private promptPremiumUpgrade(): void {
    this.alertService.info(
      'Partner classes are available to The Reserve members. View memberships in My Drop to upgrade.'
    );
    this.router.navigate(['/account', 'my-drop']);
  }

  JoinWaitlistClicked() {
    if(this.isLoggedIn())
      {
        this.currentMember = this.authService.getLoggedInMember();
        if(this.currentMember)
        {
          const request : Partial<SessionWaitlist> = {
            MemberId : this.currentMember.Id,
            SessionId: this.sessionId,
            Action: Action.Add
          };
  
          if(!this.isSessionBooked)
          {
            this.inviteCodeSubscription = this.sessionWaitlistService.add(request).subscribe((result: boolean) => {
              if(result)
              {
                this.isInWaitlist = true;
                this.cdr.markForCheck();
                //open filter popup
                const modalRef = this.modalService.open(SessionWaitlistAddedComponent, {
                  ariaLabelledBy: 'modal-basic-title',
                  // size: 'lg',
                  centered: true,
                  fullscreen: window.innerWidth <= 768,
                  // windowClass: 'custom-class'
                });

                modalRef.componentInstance.classPhoto = this.sessionDetails?.ClassPhoto;
                modalRef.componentInstance.heading = "You're on the Waitlist!";
                modalRef.componentInstance.subHeading = "We will notify everyone on the waitlist as soon as a spot becomes available.";
                //Retrieving data from popup
                modalRef.result.then((result: boolean) => {
                  
                });
              }
              
            });
          }
        }
       } else {
        // Set flag to indicate user wants to join waitlist after login
        this.pendingWaitlistAction = true;
        this.pendingPremiumUpgradeAction = false;
        this.loginPopup.open(this.sessionDetails.Id);
       }
  }

  CancelWaitlistClicked() {
    if(this.isLoggedIn())
      {
        this.currentMember = this.authService.getLoggedInMember();
        if(this.currentMember)
        {
          this.isInWaitlist = false;
          const request : Partial<SessionWaitlist> = {
            MemberId : this.currentMember.Id,
            SessionId: this.sessionId,
            Action: Action.Remove
          };
  
          if(!this.isSessionBooked)
          {
            this.inviteCodeSubscription = this.sessionWaitlistService.remove(request).subscribe((result: boolean) => {
              if(result)
              {
                //open filter popup
                const modalRef = this.modalService.open(SessionWaitlistAddedComponent, {
                  ariaLabelledBy: 'modal-basic-title',
                  // size: 'lg',
                  centered: true,
                  fullscreen: window.innerWidth <= 768,
                  // windowClass: 'custom-class'
                });

                modalRef.componentInstance.classPhoto = this.sessionDetails?.ClassPhoto;
                modalRef.componentInstance.heading = "You're removed from the Waitlist!";
                modalRef.componentInstance.subHeading = "";
                //Retrieving data from popup
                modalRef.result.then((result: boolean) => {
                  this.isInWaitlist = false;
                });
              }
              
            });
          }
        }
       } else {
        this.loginPopup.open(this.sessionDetails.Id);
       }
  }

  private getClassCurrencyId(): number {
    return this.sessionDetails?.Class?.CurrencyId || 1;
  }

  private getSubscriptionForClass(): Booking | undefined {
    const currencyId = this.getClassCurrencyId();
    return this.myDrop?.Subscriptions?.find(
      (subscription) =>
        subscription.CurrencyId === currencyId &&
        subscription.Action !== MembershipAction.Canceled
    );
  }

  isMembershipPausedForClass(): boolean {
    if (this.memberBalance?.MembershipState === 'Paused') {
      return true;
    }
    const subscription = this.getSubscriptionForClass();
    if (!subscription) {
      return false;
    }
    // Booking-only rule (matches ClassMember/DebitCurrency API): only an active pause blocks booking.
    // PauseInitiated is a scheduled pause at cycle end; members may still book until it activates.
    // Account UI (e.g. My Drop) may still label PauseInitiated as "Paused" for subscription status display.
    return subscription.Action === MembershipAction.Paused;
  }

  private hasPackCreditsForClass(): boolean {
    const currencyId = this.getClassCurrencyId();
    const now = Date.now();
    return (this.myDrop?.Packs || []).some(
      (pack) =>
        pack.CurrencyId === currencyId &&
        !pack.IsCancelled &&
        new Date(pack.EndAt).getTime() > now &&
        pack.Balance > 0
    );
  }

  private async refreshMembershipData(): Promise<void> {
    if (!this.isLoggedIn()) {
      return;
    }
    const currencyId = this.getClassCurrencyId();
    this.myDrop = await this.memberService.getMyDrop(this.currentMember.Id);
    this.memberBalance = await firstValueFrom(
      this.memberService.getBalance(this.currentMember.Id, currencyId)
    );
    this.cdr.markForCheck();
  }

  getBalance(): string {
    if (this.isMembershipPausedForClass() && !this.hasPackCreditsForClass()) {
      return 'Paused';
    }

    if(this.memberBalance && this.memberBalance.IsFreeCredits) {
      return 'Free Credits';
    }

    if(this.memberBalance && this.memberBalance.IsUnlimited) {
      return 'Unlimited';
    }

    if (this.memberBalance && this.memberBalance.Balance > 0) {
      return this.memberBalance.Balance.toString();
    }

    return '0';
  }

  getBannerImageStyle(): any {
    const imageUrl = this.sessionDetails?.ClassPhoto || 'assets/images/No-Image-Placeholder.png';
    const isPlaceholder = !this.sessionDetails?.ClassPhoto;
    
    const style: any = {
      'background-image': `url(${imageUrl})`
    };

    if (isPlaceholder) {
      style['background-size'] = 'contain';
      style['background-color'] = '#EFEFEF';
    }

    return style;
  }

  private getMemberId(): number {
    const member = this.authService.getLoggedInMember();
    return member?.Id || 0; // 0 for guest users
  }

  isFavorite(): boolean {
    if (!this.sessionDetails?.Id) {
      return false;
    }
    const memberId = this.getMemberId();
    return this.localStorageService.isFavorite(memberId, 'class', this.sessionDetails.ClassId);
  }

  toggleFavorite(): void {
    if (!this.sessionDetails?.Id) {
      return;
    }
    const memberId = this.getMemberId();
    this.localStorageService.toggleFavorite(memberId, 'class', this.sessionDetails.ClassId);
    this.cdr.markForCheck();
  }
}